import { useEffect, useState } from 'react';
import { type RecipeComment } from '../../../api/Types';
import { apiModules, supabase } from '../../../api/apiService';
import {
  appendCommentIfMissing,
  getCommentsErrorMessage,
  handleMissingRealtimeComment,
  handleRealtimeCommentLoadError,
  listCommentAuthorIds,
  mergeUpdatedAuthor,
  removeCommentById,
  replaceCommentById,
} from '../helpers';

const getRealtimeComment = (commentId: number): Promise<RecipeComment | null> =>
  apiModules.comments.getCommentById(commentId);

interface UseRecipeCommentsResult {
  comments: RecipeComment[];
  loading: boolean;
  error: string;
  setComments: React.Dispatch<React.SetStateAction<RecipeComment[]>>;
}

const useRecipeComments = (recipeId: string): UseRecipeCommentsResult => {
  const [comments, setComments] = useState<RecipeComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchComments = async () => {
      try {
        setLoading(true);
        setError('');
        const fetchedComments = await apiModules.comments.getComments(recipeId);
        setComments(fetchedComments);
      } catch (error: unknown) {
        setError(getCommentsErrorMessage(error));
      } finally {
        setLoading(false);
      }
    };

    void fetchComments();
  }, [recipeId]);

  useEffect(() => {
    const channel = supabase
      .channel(`comments_for_recipe_${recipeId}`)
      .on<RecipeComment>(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'comments',
          filter: `recipe_id=eq.${recipeId}`,
        },
        (payload) => {
          const newCommentData = payload.new as RecipeComment;
          const oldCommentData = payload.old as RecipeComment;

          switch (payload.eventType) {
            case 'INSERT':
              getRealtimeComment(newCommentData.id)
                .then((completeComment) => {
                  if (!completeComment) {
                    handleMissingRealtimeComment('new', newCommentData);
                    return;
                  }

                  setComments((prevComments) => appendCommentIfMissing(prevComments, completeComment));
                })
                .catch((error) => handleRealtimeCommentLoadError('new', error));
              break;
            case 'UPDATE':
              getRealtimeComment(newCommentData.id)
                .then((completeComment) => {
                  if (!completeComment) {
                    handleMissingRealtimeComment('updated', newCommentData);
                    return;
                  }

                  setComments((prevComments) =>
                    replaceCommentById(prevComments, newCommentData.id, completeComment)
                  );
                })
                .catch((error) => handleRealtimeCommentLoadError('updated', error));
              break;
            case 'DELETE':
              setComments((prevComments) => removeCommentById(prevComments, oldCommentData.id));
              break;
            default:
              break;
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [recipeId]);

  useEffect(() => {
    const authorIds = listCommentAuthorIds(comments);

    const channels = authorIds.map((authorId) =>
      supabase
        .channel(`comment_author_update:${authorId}`)
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'authors', filter: `id=eq.${authorId}` },
          (payload: {
            new: {
              id: number;
              name: string;
              first_name: string;
              avatar_url: string;
              color: string;
              created_at: string;
            };
          }) => {
            setComments((prevComments) =>
              prevComments.map((comment) => mergeUpdatedAuthor(comment, payload.new))
            );
          }
        )
        .subscribe()
    );

    return () => {
      channels.forEach((channel) => {
        supabase.removeChannel(channel);
      });
    };
  }, [comments]);

  return {
    comments,
    loading,
    error,
    setComments,
  };
};

export default useRecipeComments;
